import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from 'react-query';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@parrot-software/design-system';
import type { ReportSection } from '@reports/domain/value-objects';

interface ReportTabsProps {
  activeSectionKey: string;
  sections: ReportSection[];
  onSectionChange?: (sectionKey: string) => void;
  children?: (section: ReportSection) => React.ReactNode;
}

/**
 * ReportTabs - Tabs navigation component for report sections
 *
 * Displays tabs for each section of a report with rounded corners.
 * Active tab has white background with shadow, inactive tabs have light grey background.
 * Receives active section from parent to enable URL synchronization.
 *
 * @param {ReportTabsProps} props - Component properties
 * @param {string} props.activeSectionKey - Currently active section key (controlled)
 * @param {ReportSection[]} props.sections - Array of report sections to display as tabs
 * @param {(sectionKey: string) => void} [props.onSectionChange] - Callback when active section changes
 * @param {(section: ReportSection) => React.ReactNode} [props.children] - Render function for section content
 * @returns {React.ReactElement} Tabs component with section navigation
 *
 * @example
 * <ReportTabs
 *   activeSectionKey={activeSectionKey}
 *   sections={report.sections}
 *   onSectionChange={(key) => handleSectionChange(key)}
 * >
 *   {(section) => <ReportSection section={section} />}
 * </ReportTabs>
 */
export const ReportTabs = ({ activeSectionKey, sections, onSectionChange, children }: ReportTabsProps) => {
  const { t } = useTranslation(['reports', 'reportSales']);
  const isFiltersLoading = useIsFetching(['reports', 'filter-catalog']) > 0;

  const handleSectionChange = useCallback(
    (sectionKey: string) => {
      onSectionChange?.(sectionKey);
    },
    [onSectionChange],
  );

  if (sections.length === 0) {
    return null;
  }

  return (
    <Tabs onValueChange={handleSectionChange} value={activeSectionKey}>
      <TabsList>
        {sections.map((section) => (
          <TabsTrigger disabled={isFiltersLoading} key={section.key} value={section.key}>
            {t(section.title)}
          </TabsTrigger>
        ))}
      </TabsList>

      {sections.map((section) => (
        <TabsContent className="mt-4" key={section.key} value={section.key}>
          {children ? children(section) : <p>{t(section.title)}</p>}
        </TabsContent>
      ))}
    </Tabs>
  );
};
